import * as React from 'react';
import { ArrowRight, PartyPopper, X } from 'lucide-react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { OnboardingStep } from '@cfl/shared';

import { Button } from '@/components/cargofl-ui';
import { useGetStarted } from '@/components/get-started';
import { GUIDES, guideHref, onGuidePage, type GuideStep } from '@/lib/onboarding-guides';

const STORE = 'cfl.onboarding-guide';
const GRACE_MS = 1500;

interface Active {
  id: OnboardingStep;
  index: number;
}

/** The first VISIBLE element with this `data-guide` name — a page may render a hidden copy. */
function findTarget(name: string): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(`[data-guide="${name}"]`)) {
    if (el.getClientRects().length > 0) return el;
  }
  return null;
}

function load(): Active | null {
  try {
    const raw = sessionStorage.getItem(STORE);
    return raw ? (JSON.parse(raw) as Active) : null;
  } catch {
    return null;
  }
}
function persist(a: Active | null) {
  try {
    if (a) sessionStorage.setItem(STORE, JSON.stringify(a));
    else sessionStorage.removeItem(STORE);
  } catch {
    /* a locked-down browser: the guide just won't survive a reload */
  }
}

/**
 * Walks someone through one "Get started" step: a spotlight on the exact button or field, one plain
 * instruction, and a celebration when the checklist's own data says it is done. Starts only from the
 * checklist (`?guide=`), ends on ✕, Esc, or leaving the step's pages.
 */
export function OnboardingGuide() {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = React.useState<Active | null>(null);
  const [celebrating, setCelebrating] = React.useState<OnboardingStep | null>(null);
  const [rect, setRect] = React.useState<DOMRect | null>(null);

  const set = React.useCallback((a: Active | null) => {
    persist(a);
    setActive(a);
  }, []);

  // Start from `?guide=`, then drop it from the address. Keyed on the guide's name ONLY: re-running
  // on a new params or navigate object would restart the guide at step 1 on every render.
  const fromUrl = params.get('guide') as OnboardingStep | null;
  const latest = React.useRef({ params, pathname, navigate });
  latest.current = { params, pathname, navigate };
  React.useEffect(() => {
    if (fromUrl && fromUrl in GUIDES) {
      set({ id: fromUrl, index: 0 });
      setCelebrating(null);
      const { params: p, pathname: path, navigate: go } = latest.current;
      const rest = new URLSearchParams(p);
      rest.delete('guide');
      go(rest.size ? `${path}?${rest}` : path, { replace: true });
    } else if (!fromUrl) {
      setActive((a) => a ?? load());
    }
  }, [fromUrl, set]);

  const guide = active ? GUIDES[active.id] : undefined;
  const step = guide?.steps[Math.min(active!.index, guide.steps.length - 1)];

  // Leaving the guide's pages ends it — it must not follow anyone around.
  const onItsPages = !guide || onGuidePage(guide, pathname);
  React.useEffect(() => {
    if (!onItsPages) set(null);
  }, [onItsPages, set]);

  // The "well done" box belongs to the page it appeared on; moving on closes it.
  const celebratedOn = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!celebrating) celebratedOn.current = null;
    else if (celebratedOn.current === null) celebratedOn.current = pathname;
    else if (celebratedOn.current !== pathname) setCelebrating(null);
  }, [celebrating, pathname]);

  // Esc ends the guide, like any other box.
  React.useEffect(() => {
    if (!active && !celebrating) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      set(null);
      setCelebrating(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, celebrating, set]);

  const finish = React.useCallback(
    (id: OnboardingStep) => {
      setCelebrating(id);
      set(null);
    },
    [set],
  );

  // Follow the target as dialogs open, the page scrolls or the layout moves; step back if it went away.
  const missingSince = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!active || !guide || !step || !onItsPages) return;
    const tick = () => {
      const el = findTarget(step.target);
      if (el) {
        missingSince.current = null;
        const r = el.getBoundingClientRect();
        setRect((prev) => (prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height ? prev : r));
        return;
      }
      setRect(null);
      missingSince.current ??= Date.now();
      if (Date.now() - missingSince.current > GRACE_MS) {
        for (let i = active.index - 1; i >= 0; i--) {
          if (findTarget(guide.steps[i]!.target)) {
            missingSince.current = null;
            set({ ...active, index: i });
            return;
          }
        }
      }
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [active, guide, step, onItsPages, set]);

  // Move on when the person does the thing. Capture phase, so it counts before a dialog reacts; a
  // press counts on pointerdown too, because some menus open on it and never see a click.
  React.useEffect(() => {
    if (!active || !guide || !step || step.advance === 'next') return;
    const last = active.index >= guide.steps.length - 1;
    const onEvent = (e: Event) => {
      const el = (e.target as Element | null)?.closest?.(`[data-guide="${step.target}"]`);
      if (!el || last) return;
      if (step.advance === 'input' && ((el as HTMLInputElement).value ?? '').trim().length < 2) return;
      set({ ...active, index: active.index + 1 });
    };
    const types = step.advance === 'click' ? ['pointerdown', 'click'] : ['input'];
    for (const t of types) document.addEventListener(t, onEvent, true);
    return () => {
      for (const t of types) document.removeEventListener(t, onEvent, true);
    };
  }, [active, guide, step, set]);

  if (celebrating) return <Celebration message={GUIDES[celebrating].success} onClose={() => setCelebrating(null)} />;
  if (!active || !guide || !step || !onItsPages) return null;
  return (
    <>
    <DoneWatcher id={active.id} onDone={finish} />
    <Spotlight
      rect={rect}
      stepNo={Math.min(active.index, guide.steps.length - 1) + 1}
      of={guide.steps.length}
      step={step}
      onNext={() => set({ ...active, index: active.index + 1 })}
      onSkip={() => set(null)}
    />
    </>
  );
}

/**
 * Done is a fact, not a click: the same data that ticks the checklist. Mounted only while a guide
 * runs, so nobody else pays for the checklist's queries on every page.
 */
function DoneWatcher({ id, onDone }: { id: OnboardingStep; onDone: (id: OnboardingStep) => void }) {
  const done = useGetStarted().find((s) => s.key === id)?.done;
  React.useEffect(() => {
    if (done) onDone(id);
  }, [done, id, onDone]);
  return null;
}

const PAD = 6;
const CARD_W = 320;

function Spotlight({ rect, stepNo, of, step, onNext, onSkip }: { rect: DOMRect | null; stepNo: number; of: number; step: GuideStep; onNext: () => void; onSkip: () => void }) {
  // An open dialog or menu closes on any press outside it and can swallow the click that follows;
  // so the card keeps its presses to itself, and ✕ acts on the press, not on a click that may never come.
  const card = React.useRef<HTMLDivElement>(null);
  const skip = React.useRef(onSkip);
  skip.current = onSkip;
  React.useEffect(() => {
    const el = card.current;
    if (!el) return;
    const onPress = (e: Event) => {
      e.stopPropagation();
      if ((e.target as Element | null)?.closest?.('[data-guide-skip]')) skip.current();
    };
    el.addEventListener('pointerdown', onPress);
    return () => el.removeEventListener('pointerdown', onPress);
  }, []);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(CARD_W, vw - 32);
  const below = !rect || rect.bottom + 180 < vh;
  const style: React.CSSProperties = rect
    ? {
        width,
        left: Math.max(16, Math.min(rect.left, vw - width - 16)),
        ...(below ? { top: rect.bottom + PAD + 10 } : { bottom: vh - rect.top + PAD + 10 }),
      }
    : { width, left: (vw - width) / 2, bottom: 24 };

  return (
    <>
      {rect && (
        <div
          aria-hidden
          className="pointer-events-none fixed z-[60] rounded-lg ring-2 ring-primary transition-all duration-200"
          style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2, boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.45)' }}
        />
      )}
      <div ref={card} role="dialog" aria-live="polite" aria-label="Guide" className="pointer-events-auto fixed z-[61] space-y-2 rounded-xl border bg-popover p-4 text-popover-foreground shadow-lg" style={style}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-primary">Step {stepNo} of {of}</span>
          {/* onClick too, for the keyboard (Enter / Space never send a pointerdown). */}
          <Button variant="ghost" size="icon" className="size-7" aria-label="Skip the guide" data-guide-skip onClick={onSkip}>
            <X className="size-4" />
          </Button>
        </div>
        <p className="font-semibold">{step.title}</p>
        <p className="text-sm text-muted-foreground">{rect ? step.body : 'Waiting for this part of the page to appear…'}</p>
        {step.advance === 'next' && (
          <div className="flex justify-end">
            <Button size="sm" onClick={onNext}>
              Next <ArrowRight className="size-4" />
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

function Celebration({ message, onClose }: { message: string; onClose: () => void }) {
  const next = useGetStarted().find((s) => s.done === false);
  return (
    <div role="status" className="pointer-events-auto fixed inset-x-4 bottom-6 z-[61] mx-auto max-w-sm space-y-3 rounded-xl border bg-popover p-5 text-center text-popover-foreground shadow-lg">
      <Button variant="ghost" size="icon" className="absolute right-2 top-2 size-7" aria-label="Close" onClick={onClose}>
        <X className="size-4" />
      </Button>
      <PartyPopper className="mx-auto size-8 text-primary" aria-hidden />
      <p className="text-base font-semibold">🎉 {message}</p>
      <div className="flex flex-wrap justify-center gap-2">
        {next && (
          <Button size="sm" asChild onClick={onClose}>
            <Link to={guideHref(next.key, next.href)}>
              Next: {next.title} <ArrowRight className="size-4" />
            </Link>
          </Button>
        )}
        <Button size="sm" variant="outline" asChild onClick={onClose}>
          <Link to="/dashboard">Back to Get started</Link>
        </Button>
      </div>
    </div>
  );
}
